// Branded, printable invoice. Opens a self-contained A4 document in a new window
// and triggers the browser print dialog (Save as PDF) so an admin can download or
// send the invoice manually — independent of the emailed copy. No backend/PDF lib.
import toast from 'react-hot-toast';

export interface PrintLineItem {
  description: string;
  amount: string | number;
  type?: string;
}
export interface PrintInvoice {
  invoiceNumber: string;
  status: string;
  currency: string;
  totalAmount: string | number;
  amountPaid?: string | number;
  balanceDue: string | number;
  issueDate?: string;
  dueDate?: string;
  lineItems?: PrintLineItem[];
}
export interface PrintBillTo {
  name?: string;
  email?: string;
  country?: string;
}

const ISSUER = {
  name: 'KaziPeople',
  tagline: 'HR & Payroll Platform',
  email: 'billing@kazipeople.com',
  site: 'kazipeople.com',
};

const money = (v: any, cur: string) =>
  `${cur} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const esc = (s: any) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const STATUS_LABEL: Record<string, string> = {
  paid: 'PAID', sent: 'SENT', issued: 'ISSUED',
  partially_paid: 'PARTIALLY PAID', overdue: 'OVERDUE', void: 'VOID',
};

export function buildInvoiceHtml(inv: PrintInvoice, billTo: PrintBillTo): string {
  const cur = inv.currency || 'TZS';
  const items = inv.lineItems?.length ? inv.lineItems : [{ description: 'Subscription', amount: inv.totalAmount }];
  const rows = items
    .map(
      (it) => `<tr>
        <td>${esc(it.description)}</td>
        <td class="num">${money(it.amount, cur)}</td>
      </tr>`,
    )
    .join('');
  const statusLabel = STATUS_LABEL[inv.status] || inv.status.toUpperCase();
  const isPaid = inv.status === 'paid';

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Invoice ${esc(inv.invoiceNumber)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #2F3443; margin: 0; padding: 32px; font-size: 13px; }
  .sheet { max-width: 720px; margin: 0 auto; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #F97316; padding-bottom: 18px; }
  .brand { font-size: 22px; font-weight: 800; color: #F97316; letter-spacing: -0.5px; }
  .brand small { display: block; font-size: 11px; font-weight: 500; color: #6B7280; letter-spacing: 0; }
  .doc { text-align: right; }
  .doc h1 { margin: 0; font-size: 24px; letter-spacing: 2px; color: #2F3443; }
  .doc .no { color: #6B7280; margin-top: 2px; font-size: 13px; }
  .badge { display: inline-block; margin-top: 8px; padding: 3px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px;
           background: ${isPaid ? '#DCFCE7' : '#FEF3C7'}; color: ${isPaid ? '#166534' : '#92400E'}; }
  .meta { display: flex; justify-content: space-between; margin-top: 24px; gap: 24px; }
  .meta .col { flex: 1; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.6px; color: #9CA3AF; margin-bottom: 4px; }
  .meta .val { font-weight: 600; }
  table.items { width: 100%; border-collapse: collapse; margin-top: 28px; }
  table.items th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.6px; color: #9CA3AF; border-bottom: 2px solid #E5E7EB; padding: 8px 0; }
  table.items th.num, table.items td.num { text-align: right; }
  table.items td { padding: 11px 0; border-bottom: 1px solid #F1F2F4; }
  .totals { margin-top: 16px; margin-left: auto; width: 280px; }
  .totals .row { display: flex; justify-content: space-between; padding: 6px 0; }
  .totals .grand { border-top: 2px solid #2F3443; margin-top: 6px; padding-top: 10px; font-size: 16px; font-weight: 800; }
  .totals .due { color: ${isPaid ? '#166534' : '#B91C1C'}; }
  .pay { margin-top: 28px; background: #FFF7ED; border: 1px solid #FED7AA; border-radius: 10px; padding: 14px 16px; font-size: 12px; color: #7C2D12; }
  .foot { margin-top: 36px; padding-top: 16px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; text-align: center; }
  @media print { body { padding: 0; } @page { margin: 18mm; } }
</style></head>
<body><div class="sheet">
  <div class="top">
    <div class="brand">${ISSUER.name}<small>${ISSUER.tagline}</small></div>
    <div class="doc">
      <h1>INVOICE</h1>
      <div class="no">${esc(inv.invoiceNumber)}</div>
      <div class="badge">${esc(statusLabel)}</div>
    </div>
  </div>

  <div class="meta">
    <div class="col">
      <div class="label">Billed To</div>
      <div class="val">${esc(billTo.name || 'Your Company')}</div>
      <div>${esc(billTo.email || '')}</div>
      ${billTo.country ? `<div>${esc(billTo.country)}</div>` : ''}
    </div>
    <div class="col">
      <div class="label">From</div>
      <div class="val">${ISSUER.name}</div>
      <div>${ISSUER.email}</div>
      <div>${ISSUER.site}</div>
    </div>
    <div class="col">
      <div class="label">Issued</div>
      <div class="val">${fmtDate(inv.issueDate)}</div>
      <div class="label" style="margin-top:10px">Due</div>
      <div class="val">${fmtDate(inv.dueDate)}</div>
    </div>
  </div>

  <table class="items">
    <thead><tr><th>Description</th><th class="num">Amount</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals">
    <div class="row"><span>Subtotal</span><span>${money(inv.totalAmount, cur)}</span></div>
    ${Number(inv.amountPaid || 0) > 0 ? `<div class="row"><span>Amount paid</span><span>-${money(inv.amountPaid, cur)}</span></div>` : ''}
    <div class="row grand"><span>${isPaid ? 'Total' : 'Balance due'}</span><span class="due">${money(isPaid ? inv.totalAmount : inv.balanceDue, cur)}</span></div>
  </div>

  ${
    isPaid
      ? `<div class="pay">Thank you — this invoice has been paid in full.</div>`
      : `<div class="pay"><strong>How to pay:</strong> Sign in to your KaziPeople company portal → Settings → Billing → <strong>Pay now</strong> (AzamPay). For bank transfer or queries, contact ${ISSUER.email}.</div>`
  }

  <div class="foot">${ISSUER.name} · ${ISSUER.email} · ${ISSUER.site}</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;
}

export function printInvoice(inv: PrintInvoice, billTo: PrintBillTo): void {
  const w = window.open('', '_blank', 'width=820,height=960');
  if (!w) {
    toast.error('Please allow pop-ups to print or download the invoice.');
    return;
  }
  w.document.write(buildInvoiceHtml(inv, billTo));
  w.document.close();
  w.focus();
}
